import React from 'react';
import {
  Box,
  Typography,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  TextField,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormHelperText,
  Autocomplete,
  CircularProgress,
} from '@mui/material';
import { InfoOutlined, DeleteOutline } from '@mui/icons-material';
import './index.css';

const firstRowCriteriaOptions = [
  { value: 'all_patients', label: 'All Patients' },
  ...[
    { value: 'age_at_collection', label: 'Age at Time of Collection' },
    { value: 'birth_weight', label: 'Birth Weight' },
    { value: 'gestational_age', label: 'Gestational Age' },
  ].sort((a, b) => a.label.localeCompare(b.label)),
];

const additionalRowCriteriaOptions = [
  { value: 'age_at_collection', label: 'Age at Time of Collection' },
  { value: 'birth_weight', label: 'Birth Weight' },
  { value: 'gestational_age', label: 'Gestational Age' },
].sort((a, b) => a.label.localeCompare(b.label));

const birthWeightComparisonOptions = [
  { value: 'greater_than', label: 'is greater than' },
  { value: 'greater_than_or_equal_to', label: 'is greater than or equal to' },
  { value: 'less_than', label: 'is less than' },
  { value: 'less_than_or_equal_to', label: 'is less than or equal to' },
  { value: 'between', label: 'is between' },
];

const ageAndGestationalComparisonOptions = [
  { value: 'greater_than', label: 'is greater than' },
  { value: 'greater_than_or_equal_to', label: 'is greater than or equal to' },
  { value: 'less_than', label: 'is less than' },
  { value: 'less_than_or_equal_to', label: 'is less than or equal to' },
  { value: 'between', label: 'is between' },
];

const betweenComparisonOptions = [
  { value: 'greater_than', label: 'is greater than' },
  { value: 'greater_than_or_equal_to', label: 'is greater than or equal to' },
  { value: 'less_than', label: 'is less than' },
  { value: 'less_than_or_equal_to', label: 'is less than or equal to' },
];

const normalRangeComparisonOptions = [
  { value: 'greater_than', label: 'is greater than' },
  { value: 'greater_than_or_equal_to', label: 'is greater than or equal to' },
  { value: 'less_than', label: 'is less than' },
  { value: 'less_than_or_equal_to', label: 'is less than or equal to' },
  { value: 'between', label: 'is between' },
];

const ratioTypeOptions = [
  { value: 'single_analyte', label: 'Single Analyte' },
  { value: 'sum', label: 'Sum of Multiple Analytes' },
];

const infoIconStyle = {
  fontSize: '1.1rem',
  color: 'var(--info-color)',
  cursor: 'pointer',
  pointerEvents: 'auto',
  marginLeft: '4px',
  marginRight: '8px',
  verticalAlign: 'middle',
};

const getCriteriaUnit = (criteria) => {
  if (criteria === 'birth_weight') return 'grams';
  if (criteria === 'age_at_collection') return 'days';
  if (criteria === 'gestational_age') return 'weeks';
  return '';
};

const getComparisonOptionsByCriteria = (criteria) => {
  if (criteria === 'birth_weight') return birthWeightComparisonOptions;
  if (criteria === 'age_at_collection' || criteria === 'gestational_age') {
    return ageAndGestationalComparisonOptions;
  }
  return [];
};

const CutoffDialog = ({
  open,
  title,
  values,
  errors,
  saving = false,
  analyteLookupOptions,
  onClose,
  onSave,
  onValueChange,
}) => {
  // Derive display values and options for analytes based on current values and analyteLookupOptions
  const selectedAnalyte = analyteLookupOptions.find((analyteOption) => analyteOption.value === values.analyteCode);
  const selectedAnalyteShortName = selectedAnalyte?.label || 'Analyte';
  const selectedAnalyteOptions = analyteLookupOptions.filter((analyteOption) => (values.analyteCodes || []).includes(analyteOption.value));
  const numeratorSumOptions = analyteLookupOptions.filter((analyteOption) => (values.ratioNumeratorAnalyteCodes || []).includes(analyteOption.value));
  const denominatorSumOptions = analyteLookupOptions.filter((analyteOption) => (values.ratioDenominatorAnalyteCodes || []).includes(analyteOption.value));
  const numeratorSelectedCodes = values.ratioNumeratorType === 'sum'
    ? (values.ratioNumeratorAnalyteCodes || [])
    : (values.ratioNumeratorType === 'single_analyte' && values.ratioNumeratorAnalyteCode ? [values.ratioNumeratorAnalyteCode] : []);
  const denominatorSelectedCodes = values.ratioDenominatorType === 'sum'
    ? (values.ratioDenominatorAnalyteCodes || [])
    : (values.ratioDenominatorType === 'single_analyte' && values.ratioDenominatorAnalyteCode ? [values.ratioDenominatorAnalyteCode] : []);
  const numeratorSingleOption = analyteLookupOptions.find((analyteOption) => analyteOption.value === values.ratioNumeratorAnalyteCode) || null;
  const denominatorSingleOption = analyteLookupOptions.find((analyteOption) => analyteOption.value === values.ratioDenominatorAnalyteCode) || null;
  const sumExpressionLabel = selectedAnalyteOptions.map((analyteOption) => analyteOption.label).join(' + ');
  const numeratorLabelText = numeratorSelectedCodes.map((code) => analyteLookupOptions.find((option) => option.value === code)?.label || code).join(' + ');
  const denominatorLabelText = denominatorSelectedCodes.map((code) => analyteLookupOptions.find((option) => option.value === code)?.label || code).join(' + ');
  const ratioExpressionLabel = `${numeratorLabelText || 'Numerator'} / ${denominatorLabelText || 'Denominator'}`;
  const criteriaRows = Array.isArray(values.criteriaRows) && values.criteriaRows.length
    ? values.criteriaRows
    : [{ criteria: '', comparison: '', value: '', betweenComparison1: '', betweenValue1: '', betweenComparison2: '', betweenValue2: '' }];
  const normalRangeRows = Array.isArray(values.normalRangeRows) && values.normalRangeRows.length
    ? values.normalRangeRows
    : [{ normalRangeOperator: '', normalRangeValue: '', normalRangeBetweenComparison1: '', normalRangeBetweenValue1: '', normalRangeBetweenComparison2: '', normalRangeBetweenValue2: '' }];
  const firstRowIsAllPatients = criteriaRows[0]?.criteria === 'all_patients';
  const showSingleAnalyteSections = values.cutoffType === 'single_analyte' && !!values.analyteCode;
  const showSumSections = values.cutoffType === 'sum' && Array.isArray(values.analyteCodes) && values.analyteCodes.length >= 2;
  const numeratorReady = (
    (values.ratioNumeratorType === 'single_analyte' && !!values.ratioNumeratorAnalyteCode)
    || (values.ratioNumeratorType === 'sum' && Array.isArray(values.ratioNumeratorAnalyteCodes) && values.ratioNumeratorAnalyteCodes.length >= 2)
  );
  const showRatioSections = values.cutoffType === 'ratio' && (
    (values.ratioDenominatorType === 'single_analyte' && !!values.ratioDenominatorAnalyteCode)
    || (values.ratioDenominatorType === 'sum' && Array.isArray(values.ratioDenominatorAnalyteCodes) && values.ratioDenominatorAnalyteCodes.length >= 2)
  );
  const showNormalRangeFormulaLabel = showSumSections || showRatioSections;
  const normalRangeFormulaLabel = showRatioSections ? ratioExpressionLabel : (sumExpressionLabel || 'Analyte(s)');

  const renderSectionHeading = (sectionLabel, tooltipMessage) => (
    <Box className="psCutoffDialogSectionHeading">
      <Typography component="h3" className="psCutoffDialogSectionTitleH3">
        {sectionLabel}
      </Typography>
      <Tooltip title={tooltipMessage} placement="top" arrow>
        <span tabIndex={0} role="button" aria-label={tooltipMessage}>
          <InfoOutlined style={{ ...infoIconStyle, marginRight: 0 }} />
        </span>
      </Tooltip>
    </Box>
  );

  const updateCriteriaRow = (index, updates) => {
    const nextRows = criteriaRows.map((row, rowIndex) => (rowIndex === index ? { ...row, ...updates } : row));
    onValueChange('criteriaRows', nextRows);
  };

  const addCriteriaRow = () => {
    const nextRows = [
      ...criteriaRows,
      {
        criteria: '',
        comparison: '',
        value: '',
        betweenComparison1: '',
        betweenValue1: '',
        betweenComparison2: '',
        betweenValue2: '',
      },
    ];
    onValueChange('criteriaRows', nextRows);
  };

  const removeCriteriaRow = (index) => {
    const nextRows = criteriaRows.filter((_, rowIndex) => rowIndex !== index);
    onValueChange(
      'criteriaRows',
      nextRows.length > 0
        ? nextRows
        : [{ criteria: '', comparison: '', value: '', betweenComparison1: '', betweenValue1: '', betweenComparison2: '', betweenValue2: '' }],
    );
  };

  const updateNormalRangeRow = (index, updates) => {
    const nextRows = normalRangeRows.map((row, rowIndex) => {
      if (rowIndex !== index) return row;
      const nextRow = { ...row, ...updates };
      if ('normalRangeOperator' in updates) {
        if (updates.normalRangeOperator === 'between') {
          nextRow.normalRangeValue = '';
        } else {
          nextRow.normalRangeBetweenComparison1 = '';
          nextRow.normalRangeBetweenValue1 = '';
          nextRow.normalRangeBetweenComparison2 = '';
          nextRow.normalRangeBetweenValue2 = '';
        }
      }
      return nextRow;
    });
    onValueChange('normalRangeRows', nextRows);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="cutoff-dialog-title"
      maxWidth={false}
      fullWidth
      PaperProps={{
        sx: {
          height: '90vh',
          width: 'min(1280px, 98vw)',
        },
      }}
    >
      <DialogTitle id="cutoff-dialog-title" sx={{ textAlign: 'center' }}>
        <Typography component="span" className="psCutoffDialogTitleText">{title}</Typography>
        <Typography className="psCutoffDialogTitleSubheading">
          Choose a cutoff type and then specify the patient criteria and normal range.
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Box className="psCutoffDialogContent">
          <Typography variant="caption" className="psCutoffRequiredText">
            * indicates a required field
          </Typography>

          {errors.submit && (
            <Typography color="error" role="alert" sx={{ alignSelf: 'center' }}>
              {errors.submit}
            </Typography>
          )}

          <FormControl required size="small" error={!!errors.cutoffType} className="psCutoffDialogInput">
            <InputLabel id="cutoff-type-label" required>Cutoff Type</InputLabel>
            <Select
              labelId="cutoff-type-label"
              id="cutoffType"
              value={values.cutoffType}
              label="Cutoff Type"
              onChange={(e) => onValueChange('cutoffType', e.target.value)}
            >
              <MenuItem value="single_analyte">Single Analyte</MenuItem>
              <MenuItem value="sum">Sum of Multiple Analytes</MenuItem>
              <MenuItem value="ratio">Ratio</MenuItem>
            </Select>
            {errors.cutoffType && <FormHelperText>{errors.cutoffType}</FormHelperText>}
          </FormControl>

          {values.cutoffType === 'single_analyte' && (
            <FormControl required size="small" error={!!errors.analyteCode} className="psCutoffDialogInput">
              <InputLabel id="analyte-code-label" required>Analyte</InputLabel>
              <Select
                labelId="analyte-code-label"
                id="analyteCode"
                value={values.analyteCode}
                label="Analyte"
                onChange={(e) => onValueChange('analyteCode', e.target.value)}
              >
                {analyteLookupOptions.map((analyteOption) => (
                  <MenuItem key={analyteOption.value} value={analyteOption.value}>
                    {analyteOption.label}
                  </MenuItem>
                ))}
              </Select>
              {errors.analyteCode && <FormHelperText>{errors.analyteCode}</FormHelperText>}
            </FormControl>
          )}

          {values.cutoffType === 'sum' && (
            <FormControl required error={!!errors.analyteCodes} className="psCutoffDialogInput">
              <Autocomplete
                size="small"
                multiple
                id="analyteCodes"
                options={analyteLookupOptions}
                value={selectedAnalyteOptions}
                onChange={(event, newValue) => onValueChange('analyteCodes', newValue.map((option) => option.value))}
                getOptionLabel={(option) => option?.label || ''}
                isOptionEqualToValue={(option, value) => option?.value === value?.value}
                filterSelectedOptions
                renderInput={(params) => (
                  <TextField
                    {...params}
                    required
                    label="Analytes"
                    error={!!errors.analyteCodes}
                    helperText={errors.analyteCodes || ''}
                  />
                )}
              />
            </FormControl>
          )}

          {values.cutoffType === 'ratio' && (
            <>
              <FormControl required size="small" error={!!errors.ratioNumeratorType} className="psCutoffDialogInput">
                <InputLabel id="ratio-numerator-type-label" required>Numerator</InputLabel>
                <Select
                  labelId="ratio-numerator-type-label"
                  id="ratioNumeratorType"
                  value={values.ratioNumeratorType}
                  label="Numerator"
                  onChange={(e) => onValueChange('ratioNumeratorType', e.target.value)}
                >
                  {ratioTypeOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
                {errors.ratioNumeratorType && <FormHelperText>{errors.ratioNumeratorType}</FormHelperText>}
              </FormControl>

              {values.ratioNumeratorType === 'single_analyte' && (
                <FormControl required error={!!errors.ratioNumeratorAnalyteCode} className="psCutoffDialogInput">
                  <Autocomplete
                    size="small"
                    id="ratioNumeratorAnalyteCode"
                    options={analyteLookupOptions}
                    value={numeratorSingleOption}
                    onChange={(event, newValue) => onValueChange('ratioNumeratorAnalyteCode', newValue?.value || '')}
                    getOptionLabel={(option) => option?.label || ''}
                    isOptionEqualToValue={(option, value) => option?.value === value?.value}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        required
                        label="Numerator Analyte"
                        error={!!errors.ratioNumeratorAnalyteCode}
                        helperText={errors.ratioNumeratorAnalyteCode || ''}
                      />
                    )}
                  />
                </FormControl>
              )}

              {values.ratioNumeratorType === 'sum' && (
                <FormControl required error={!!errors.ratioNumeratorAnalyteCodes} className="psCutoffDialogInput">
                  <Autocomplete
                    size="small"
                    multiple
                    id="ratioNumeratorAnalyteCodes"
                    options={analyteLookupOptions}
                    value={numeratorSumOptions}
                    onChange={(event, newValue) => onValueChange('ratioNumeratorAnalyteCodes', newValue.map((option) => option.value))}
                    getOptionLabel={(option) => option?.label || ''}
                    isOptionEqualToValue={(option, value) => option?.value === value?.value}
                    filterSelectedOptions
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        required
                        label="Numerator Analytes"
                        error={!!errors.ratioNumeratorAnalyteCodes}
                        helperText={errors.ratioNumeratorAnalyteCodes || ''}
                      />
                    )}
                  />
                </FormControl>
              )}

              {numeratorReady && (
                <FormControl required size="small" error={!!errors.ratioDenominatorType} className="psCutoffDialogInput">
                  <InputLabel id="ratio-denominator-type-label" required>Denominator</InputLabel>
                  <Select
                    labelId="ratio-denominator-type-label"
                    id="ratioDenominatorType"
                    value={values.ratioDenominatorType}
                    label="Denominator"
                    onChange={(e) => onValueChange('ratioDenominatorType', e.target.value)}
                  >
                    {ratioTypeOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.ratioDenominatorType && <FormHelperText>{errors.ratioDenominatorType}</FormHelperText>}
                </FormControl>
              )}

              {numeratorReady && values.ratioDenominatorType === 'single_analyte' && (
                <FormControl required error={!!errors.ratioDenominatorAnalyteCode} className="psCutoffDialogInput">
                  <Autocomplete
                    size="small"
                    id="ratioDenominatorAnalyteCode"
                    options={analyteLookupOptions}
                    value={denominatorSingleOption}
                    onChange={(event, newValue) => onValueChange('ratioDenominatorAnalyteCode', newValue?.value || '')}
                    getOptionLabel={(option) => option?.label || ''}
                    isOptionEqualToValue={(option, value) => option?.value === value?.value}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        required
                        label="Denominator Analyte"
                        error={!!errors.ratioDenominatorAnalyteCode}
                        helperText={errors.ratioDenominatorAnalyteCode || ''}
                      />
                    )}
                  />
                </FormControl>
              )}

              {numeratorReady && values.ratioDenominatorType === 'sum' && (
                <FormControl required error={!!errors.ratioDenominatorAnalyteCodes} className="psCutoffDialogInput">
                  <Autocomplete
                    size="small"
                    multiple
                    id="ratioDenominatorAnalyteCodes"
                    options={analyteLookupOptions}
                    value={denominatorSumOptions}
                    onChange={(event, newValue) => onValueChange('ratioDenominatorAnalyteCodes', newValue.map((option) => option.value))}
                    getOptionLabel={(option) => option?.label || ''}
                    isOptionEqualToValue={(option, value) => option?.value === value?.value}
                    filterSelectedOptions
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        required
                        label="Denominator Analytes"
                        error={!!errors.ratioDenominatorAnalyteCodes}
                        helperText={errors.ratioDenominatorAnalyteCodes || ''}
                      />
                    )}
                  />
                </FormControl>
              )}
            </>
          )}

          {(showSingleAnalyteSections || showSumSections || showRatioSections) && (
            <>
              {renderSectionHeading('Specify Patient Criteria', 'Specify the criteria for the patient')}

              <Box className="psCutoffDialogCriteriaGroup">
                {criteriaRows.map((row, index) => {
                  const showExtras = !!row.criteria && row.criteria !== 'all_patients';
                  const isBetween = row.comparison === 'between';
                  const unitLabel = getCriteriaUnit(row.criteria);
                  const comparisonOptions = getComparisonOptionsByCriteria(row.criteria);
                  const usedCriteriaTypes = criteriaRows
                    .filter((r, ri) => ri !== index && r.criteria && r.criteria !== 'all_patients' && r.criteria !== '')
                    .map(r => r.criteria);
                  const availableOptions = (index === 0 ? firstRowCriteriaOptions : additionalRowCriteriaOptions)
                    .filter(opt => opt.value === 'all_patients' || !usedCriteriaTypes.includes(opt.value));

                  return (
                    <React.Fragment key={`criteria-row-${index}`}>
                      {index > 0 && <Typography className="psCutoffDialogAndLabel">AND</Typography>}

                      <Box className="psCutoffDialogCriteriaRowWrapper">
                        <Box className="psCutoffDialogCriteriaRow">
                          <FormControl required size="small" error={!!errors[`criteriaRows.${index}.criteria`]} className="psCutoffDialogInputLeft">
                            <InputLabel id={`patient-criteria-label-${index}`} required>Select Patient Criteria</InputLabel>
                            <Select
                              labelId={`patient-criteria-label-${index}`}
                              id={`patientCriteria-${index}`}
                              value={row.criteria || ''}
                              label="Select Patient Criteria"
                              onChange={(e) => updateCriteriaRow(index, {
                                criteria: e.target.value,
                                comparison: '',
                                value: '',
                                betweenComparison1: '',
                                betweenValue1: '',
                                betweenComparison2: '',
                                betweenValue2: '',
                              })}
                            >
                              {availableOptions.map((criteriaOption) => (
                                <MenuItem key={criteriaOption.value} value={criteriaOption.value}>
                                  {criteriaOption.label}
                                </MenuItem>
                              ))}
                            </Select>
                            {errors[`criteriaRows.${index}.criteria`] && (
                              <FormHelperText>{errors[`criteriaRows.${index}.criteria`]}</FormHelperText>
                            )}
                          </FormControl>

                          {showExtras && (
                            <>
                              <FormControl required size="small" error={!!errors[`criteriaRows.${index}.comparison`]} className="psCutoffDialogCriteriaComparisonSelect">
                                <InputLabel id={`patient-criteria-comparison-label-${index}`} required>Select Comparison</InputLabel>
                                <Select
                                  labelId={`patient-criteria-comparison-label-${index}`}
                                  id={`patientCriteriaComparison-${index}`}
                                  value={row.comparison || ''}
                                  label="Select Comparison"
                                  onChange={(e) => updateCriteriaRow(index, {
                                    comparison: e.target.value,
                                    value: '',
                                    betweenComparison1: '',
                                    betweenValue1: '',
                                    betweenComparison2: '',
                                    betweenValue2: '',
                                  })}
                                >
                                  {comparisonOptions.map((comparisonOption) => (
                                    <MenuItem key={comparisonOption.value} value={comparisonOption.value}>
                                      {comparisonOption.label}
                                    </MenuItem>
                                  ))}
                                </Select>
                                {errors[`criteriaRows.${index}.comparison`] && (
                                  <FormHelperText>{errors[`criteriaRows.${index}.comparison`]}</FormHelperText>
                                )}
                              </FormControl>

                              {!isBetween && (
                                <>
                                  <TextField
                                    required
                                    id={`patientCriteriaValue-${index}`}
                                    label="Amount"
                                    size="small"
                                    type="number"
                                    value={row.value ?? ''}
                                    error={!!errors[`criteriaRows.${index}.value`]}
                                    onChange={(e) => updateCriteriaRow(index, { value: e.target.value })}
                                    className="psCutoffDialogCriteriaValueInput"
                                    inputProps={{
                                      step: row.criteria === 'birth_weight' ? '1' : 'any',
                                      min: 0,
                                    }}
                                    helperText={errors[`criteriaRows.${index}.value`] || ''}
                                  />
                                  {!!unitLabel && <Typography className="psCutoffDialogUnitLabel">{unitLabel}</Typography>}
                                </>
                              )}
                            </>
                          )}

                          {index > 0 && (
                            <Tooltip title="remove criteria" placement="top" arrow>
                              <Button
                                size="small"
                                variant="outlined"
                                aria-label={`Remove additional criteria row ${index + 1}`}
                                onClick={() => removeCriteriaRow(index)}
                                className="psCutoffDialogDeleteButton"
                              >
                                <DeleteOutline fontSize="small" />
                              </Button>
                            </Tooltip>
                          )}
                        </Box>

                        {isBetween && (
                          <Box className="psCutoffDialogBetweenRow">
                            <Box className="psCutoffDialogBetweenGroup">
                              <FormControl required size="small" error={!!errors[`criteriaRows.${index}.betweenComparison1`]} className="psCutoffDialogCriteriaComparisonSelect">
                                <InputLabel id={`between-comparison-1-label-${index}`} required>Select Comparison</InputLabel>
                                <Select
                                  labelId={`between-comparison-1-label-${index}`}
                                  id={`betweenComparison1-${index}`}
                                  value={row.betweenComparison1 || ''}
                                  label="Select Comparison"
                                  onChange={(e) => updateCriteriaRow(index, { betweenComparison1: e.target.value })}
                                >
                                  {betweenComparisonOptions.map((comparisonOption) => (
                                    <MenuItem key={comparisonOption.value} value={comparisonOption.value}>
                                      {comparisonOption.label}
                                    </MenuItem>
                                  ))}
                                </Select>
                                {errors[`criteriaRows.${index}.betweenComparison1`] && (
                                  <FormHelperText>{errors[`criteriaRows.${index}.betweenComparison1`]}</FormHelperText>
                                )}
                              </FormControl>

                              <TextField
                                required
                                id={`betweenValue1-${index}`}
                                label="Amount"
                                size="small"
                                type="number"
                                value={row.betweenValue1 ?? ''}
                                error={!!errors[`criteriaRows.${index}.betweenValue1`]}
                                onChange={(e) => updateCriteriaRow(index, { betweenValue1: e.target.value })}
                                className="psCutoffDialogCriteriaValueInput"
                                inputProps={{ step: row.criteria === 'birth_weight' ? '1' : 'any', min: 0 }}
                                helperText={errors[`criteriaRows.${index}.betweenValue1`] || ''}
                              />

                              {!!unitLabel && <Typography className="psCutoffDialogUnitLabel">{unitLabel}</Typography>}
                            </Box>

                            <Typography className="psCutoffDialogAndInlineLabel">AND</Typography>

                            <Box className="psCutoffDialogBetweenGroup">
                              <FormControl required size="small" error={!!errors[`criteriaRows.${index}.betweenComparison2`]} className="psCutoffDialogCriteriaComparisonSelect">
                                <InputLabel id={`between-comparison-2-label-${index}`} required>Select Comparison</InputLabel>
                                <Select
                                  labelId={`between-comparison-2-label-${index}`}
                                  id={`betweenComparison2-${index}`}
                                  value={row.betweenComparison2 || ''}
                                  label="Select Comparison"
                                  onChange={(e) => updateCriteriaRow(index, { betweenComparison2: e.target.value })}
                                >
                                  {betweenComparisonOptions.map((comparisonOption) => (
                                    <MenuItem key={comparisonOption.value} value={comparisonOption.value}>
                                      {comparisonOption.label}
                                    </MenuItem>
                                  ))}
                                </Select>
                                {errors[`criteriaRows.${index}.betweenComparison2`] && (
                                  <FormHelperText>{errors[`criteriaRows.${index}.betweenComparison2`]}</FormHelperText>
                                )}
                              </FormControl>

                              <TextField
                                required
                                id={`betweenValue2-${index}`}
                                label="Amount"
                                size="small"
                                type="number"
                                value={row.betweenValue2 ?? ''}
                                error={!!errors[`criteriaRows.${index}.betweenValue2`]}
                                onChange={(e) => updateCriteriaRow(index, { betweenValue2: e.target.value })}
                                className="psCutoffDialogCriteriaValueInput"
                                inputProps={{ step: row.criteria === 'birth_weight' ? '1' : 'any', min: 0 }}
                                helperText={errors[`criteriaRows.${index}.betweenValue2`] || ''}
                              />

                              {!!unitLabel && <Typography className="psCutoffDialogUnitLabel">{unitLabel}</Typography>}
                            </Box>
                          </Box>
                        )}
                      </Box>
                    </React.Fragment>
                  );
                })}

                {!firstRowIsAllPatients && (() => {
                  const maxReached = criteriaRows.length >= 3;
                  const MAX_CRITERIA_MESSAGE = 'The maximum amount of criteria have been added';
                  return (
                    <Box className="psCutoffDialogAdditionalCriteriaButtonRow" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Button size="small" variant="outlined" onClick={addCriteriaRow} disabled={maxReached}>
                        Add Patient Criteria
                      </Button>
                      {maxReached && (
                        <Tooltip title={MAX_CRITERIA_MESSAGE} placement="top" arrow>
                          <span tabIndex={0} role="img" aria-label={MAX_CRITERIA_MESSAGE} style={{ display: 'inline-flex', alignItems: 'center' }}>
                            <InfoOutlined style={infoIconStyle} />
                          </span>
                        </Tooltip>
                      )}
                    </Box>
                  );
                })()}
              </Box>

              {renderSectionHeading('Specify Normal Range', 'Specify the normal range for the analyte')}

              {showNormalRangeFormulaLabel && (
                <Typography className="psCutoffDialogFormulaLabel">{normalRangeFormulaLabel}</Typography>
              )}

              <Box className="psCutoffDialogCriteriaGroup">
                {(() => {
                  const row = normalRangeRows[0] || { normalRangeOperator: '', normalRangeValue: '', normalRangeBetweenComparison1: '', normalRangeBetweenValue1: '', normalRangeBetweenComparison2: '', normalRangeBetweenValue2: '' };
                  const isBetween = row.normalRangeOperator === 'between';
                  return (
                    <Box className="psCutoffDialogCriteriaRowWrapper">
                      <Box className="psCutoffDialogNormalRangeRow">
                        {(showSingleAnalyteSections || showSumSections || showRatioSections) && (
                          <Typography className="psCutoffDialogAnalyteLabel">
                            {showSingleAnalyteSections ? selectedAnalyteShortName : (showSumSections ? 'Sum' : 'Ratio')}
                          </Typography>
                        )}

                        <FormControl
                          required
                          size="small"
                          error={!!errors['normalRangeRows.0.normalRangeOperator']}
                          className="psCutoffDialogOperatorSelect"
                        >
                          <InputLabel id="normal-range-operator-label-0" required>Select Comparison</InputLabel>
                          <Select
                            labelId="normal-range-operator-label-0"
                            id="normalRangeOperator-0"
                            value={row.normalRangeOperator || ''}
                            label="Select Comparison"
                            onChange={(e) => updateNormalRangeRow(0, { normalRangeOperator: e.target.value })}
                          >
                            {normalRangeComparisonOptions.map((comparisonOption) => (
                              <MenuItem key={comparisonOption.value} value={comparisonOption.value}>
                                {comparisonOption.label}
                              </MenuItem>
                            ))}
                          </Select>
                          {errors['normalRangeRows.0.normalRangeOperator'] && (
                            <FormHelperText>{errors['normalRangeRows.0.normalRangeOperator']}</FormHelperText>
                          )}
                        </FormControl>

                        {!isBetween && (
                          <>
                            <TextField
                              required
                              id="normalRangeValue-0"
                              label="Amount"
                              size="small"
                              type="number"
                              value={row.normalRangeValue ?? ''}
                              error={!!errors['normalRangeRows.0.normalRangeValue']}
                              onChange={(e) => updateNormalRangeRow(0, { normalRangeValue: e.target.value })}
                              className="psCutoffDialogValueInput"
                              inputProps={{ step: 'any', min: 0 }}
                              helperText={errors['normalRangeRows.0.normalRangeValue'] || ''}
                            />
                            <Typography className="psCutoffDialogUnitLabel">umol/L</Typography>
                          </>
                        )}
                      </Box>

                      {isBetween && (
                        <Box className="psCutoffDialogBetweenRow">
                          <Box className="psCutoffDialogBetweenGroup">
                            <FormControl required size="small" error={!!errors['normalRangeRows.0.normalRangeBetweenComparison1']} className="psCutoffDialogCriteriaComparisonSelect">
                              <InputLabel id="nr-between-comparison-1-label" required>Select Comparison</InputLabel>
                              <Select
                                labelId="nr-between-comparison-1-label"
                                id="nrBetweenComparison1"
                                value={row.normalRangeBetweenComparison1 || ''}
                                label="Select Comparison"
                                onChange={(e) => updateNormalRangeRow(0, { normalRangeBetweenComparison1: e.target.value })}
                              >
                                {betweenComparisonOptions.map((comparisonOption) => (
                                  <MenuItem key={comparisonOption.value} value={comparisonOption.value}>
                                    {comparisonOption.label}
                                  </MenuItem>
                                ))}
                              </Select>
                              {errors['normalRangeRows.0.normalRangeBetweenComparison1'] && (
                                <FormHelperText>{errors['normalRangeRows.0.normalRangeBetweenComparison1']}</FormHelperText>
                              )}
                            </FormControl>

                            <TextField
                              required
                              id="nrBetweenValue1"
                              label="Amount"
                              size="small"
                              type="number"
                              value={row.normalRangeBetweenValue1 ?? ''}
                              error={!!errors['normalRangeRows.0.normalRangeBetweenValue1']}
                              onChange={(e) => updateNormalRangeRow(0, { normalRangeBetweenValue1: e.target.value })}
                              className="psCutoffDialogCriteriaValueInput"
                              inputProps={{ step: 'any', min: 0 }}
                              helperText={errors['normalRangeRows.0.normalRangeBetweenValue1'] || ''}
                            />

                            <Typography className="psCutoffDialogUnitLabel">umol/L</Typography>
                          </Box>

                          <Typography className="psCutoffDialogAndInlineLabel">AND</Typography>

                          <Box className="psCutoffDialogBetweenGroup">
                            <FormControl required size="small" error={!!errors['normalRangeRows.0.normalRangeBetweenComparison2']} className="psCutoffDialogCriteriaComparisonSelect">
                              <InputLabel id="nr-between-comparison-2-label" required>Select Comparison</InputLabel>
                              <Select
                                labelId="nr-between-comparison-2-label"
                                id="nrBetweenComparison2"
                                value={row.normalRangeBetweenComparison2 || ''}
                                label="Select Comparison"
                                onChange={(e) => updateNormalRangeRow(0, { normalRangeBetweenComparison2: e.target.value })}
                              >
                                {betweenComparisonOptions.map((comparisonOption) => (
                                  <MenuItem key={comparisonOption.value} value={comparisonOption.value}>
                                    {comparisonOption.label}
                                  </MenuItem>
                                ))}
                              </Select>
                              {errors['normalRangeRows.0.normalRangeBetweenComparison2'] && (
                                <FormHelperText>{errors['normalRangeRows.0.normalRangeBetweenComparison2']}</FormHelperText>
                              )}
                            </FormControl>

                            <TextField
                              required
                              id="nrBetweenValue2"
                              label="Amount"
                              size="small"
                              type="number"
                              value={row.normalRangeBetweenValue2 ?? ''}
                              error={!!errors['normalRangeRows.0.normalRangeBetweenValue2']}
                              onChange={(e) => updateNormalRangeRow(0, { normalRangeBetweenValue2: e.target.value })}
                              className="psCutoffDialogCriteriaValueInput"
                              inputProps={{ step: 'any', min: 0 }}
                              helperText={errors['normalRangeRows.0.normalRangeBetweenValue2'] || ''}
                            />

                            <Typography className="psCutoffDialogUnitLabel">umol/L</Typography>
                          </Box>
                        </Box>
                      )}
                    </Box>
                  );
                })()}
              </Box>

              {renderSectionHeading('Specify Flag Message', 'Specify a message for the flag')}

              <TextField
                required
                id="flagMessage"
                label="Flag Message"
                multiline
                minRows={2}
                maxRows={6}
                value={values.flagMessage ?? ''}
                error={!!errors.flagMessage}
                onChange={(e) => onValueChange('flagMessage', e.target.value)}
                className="psCutoffDialogFlagMessageInput"
                helperText={errors.flagMessage || `${(values.flagMessage ?? '').length}/200 characters`}
                inputProps={{ maxLength: 200 }}
              />
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose} disabled={saving}>Cancel</Button>
        <Button
          variant="contained"
          onClick={onSave}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {saving ? 'Saving...' : 'OK'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CutoffDialog;
